
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';

export const socialPlatforms = [
  { id: 'facebook', name: 'Facebook', icon: 'facebook', color: 'bg-blue-600' },
  { id: 'twitter', name: 'Twitter', icon: 'twitter', color: 'bg-blue-400' },
  { id: 'instagram', name: 'Instagram', icon: 'instagram', color: 'bg-pink-600' },
  { id: 'youtube', name: 'YouTube', icon: 'youtube', color: 'bg-red-600' },
  { id: 'clipboard', name: 'Copy Link', icon: 'clipboard', color: 'bg-gray-600' },
];

const shareToFacebook = async (url: string, title?: string): Promise<boolean> => {
  try {
    const shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
    window.open(shareUrl, '_blank', 'width=600,height=400');
    return true;
  } catch (error) {
    console.error('Error sharing to Facebook:', error);
    return false;
  }
};

const shareToTwitter = async (url: string, title?: string): Promise<boolean> => {
  try {
    const shareText = title ? `${title} ${url}` : url;
    const shareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}`;
    window.open(shareUrl, '_blank', 'width=600,height=400');
    return true;
  } catch (error) {
    console.error('Error sharing to Twitter:', error);
    return false;
  }
};

const shareToInstagram = async (url: string): Promise<boolean> => {
  try {
    // Instagram doesn't have a direct web sharing API
    // Copy to clipboard and provide instructions
    await navigator.clipboard.writeText(url);
    toast.info('Link copied! Open Instagram and paste in your post');
    return true;
  } catch (error) {
    console.error('Error with Instagram share:', error);
    return false;
  }
};

const shareToYouTube = async (url: string): Promise<boolean> => {
  try {
    // YouTube doesn't have a direct sharing API for external content
    window.open('https://studio.youtube.com/channel/upload', '_blank');
    await navigator.clipboard.writeText(url);
    toast.info('Link copied! Upload the video to YouTube');
    return true;
  } catch (error) {
    console.error('Error with YouTube share:', error);
    return false;
  }
};

const copyToClipboard = async (url: string): Promise<boolean> => {
  try {
    await navigator.clipboard.writeText(url);
    toast.success('Link copied to clipboard!');
    return true;
  } catch (error) {
    console.error('Error copying to clipboard:', error);
    return false;
  }
};

export const shareToSocialMedia = async (
  platform: string,
  url: string,
  title: string = '',
  description: string = '',
  userId: string,
  merchantId?: string,
  eventId?: number | null
): Promise<boolean> => {
  try {
    console.log(`Sharing to ${platform}:`, { url, title, merchantId, userId });
    
    // Share to the selected platform
    let success = false;
    
    switch (platform.toLowerCase()) {
      case 'facebook':
        success = await shareToFacebook(url, title);
        break;
      case 'twitter':
        success = await shareToTwitter(url, title);
        break;
      case 'instagram':
        success = await shareToInstagram(url);
        break;
      case 'youtube':
        success = await shareToYouTube(url);
        break;
      case 'clipboard':
        success = await copyToClipboard(url);
        break;
      default:
        toast.error('Unknown platform');
        return false;
    }
    
    if (success) {
      // Record the share in the database
      const shareData: Record<string, any> = {
        user_id: userId,
        platform: platform,
        share_link: url,
        success: true
      };
      
      if (merchantId) {
        shareData.merchant_id = merchantId;
      }
      
      if (eventId) {
        shareData.event_id = eventId;
      }
      
      // Add share record to database
      const { error: shareError } = await supabase
        .from('shares')
        .insert(shareData);
        
      if (shareError) {
        console.error('Error recording share:', shareError);
      }
      
      // If this is a merchant video, award points to the user
      if (merchantId) {
        await awardPointsForSharing(userId, merchantId);
      }
      
      return true;
    }
    
    return false;
  } catch (error) {
    console.error('Error in shareToSocialMedia:', error);
    toast.error('Failed to share');
    return false;
  }
};

// Award points to user for sharing a merchant video
const awardPointsForSharing = async (userId: string, merchantId: string): Promise<void> => {
  try {
    // Get points configuration for the merchant
    const { data: pointsConfig, error: configError } = await supabase
      .from('points_config')
      .select('follow_up_points')
      .eq('merchant_id', merchantId)
      .single();
      
    if (configError) {
      console.error('Error fetching points config:', configError);
      return;
    }
    
    const pointsToAward = pointsConfig?.follow_up_points || 0;
    
    if (pointsToAward <= 0) {
      console.log('No points configured for sharing');
      return;
    }
    
    // Get the merchant owner user ID
    const { data: merchantData, error: merchantError } = await supabase
      .from('merchants')
      .select('merchant_id')
      .eq('id', merchantId)
      .single();
      
    if (merchantError) {
      console.error('Error fetching merchant data:', merchantError);
      return;
    }
    
    const merchantOwnerId = merchantData?.merchant_id;
    
    if (!merchantOwnerId) {
      console.error('Merchant owner ID not found');
      return;
    }
    
    // Award points to user
    const { data: profileData, error: profileError } = await supabase
      .from('profile')
      .select('total_points')
      .eq('user_id', userId)
      .single();
      
    if (profileError) {
      console.error('Error fetching user profile:', profileError);
      return;
    }
    
    const currentPoints = profileData?.total_points || 0;
    const newPoints = currentPoints + pointsToAward;
    
    // Update user points
    const { error: updateError } = await supabase
      .from('profile')
      .update({ total_points: newPoints })
      .eq('user_id', userId);
      
    if (updateError) {
      console.error('Error updating user points:', updateError);
      return;
    }
    
    // Record points transaction
    const { error: pointsError } = await supabase
      .from('points')
      .insert({
        user_id: userId,
        points_amt: pointsToAward,
      });
      
    if (pointsError) {
      console.error('Error recording points transaction:', pointsError);
      return;
    }
    
    toast.success(`You earned ${pointsToAward} points for sharing!`);
    
  } catch (error) {
    console.error('Error awarding points:', error);
  }
};
